import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import {
    Platform,
    StyleSheet,
    Text,
    View,
    Image,
    TouchableOpacity,
    FlatList,
    Modal
} from "react-native";
import AppTheme from "../../utils/Theme";
import SVGView from "../../utils/SvgView";
import { default as IcPlay } from "../../assets/svg/icons/solid/play.svg";
import { getLanguage } from "../../utils/Localization/Localization";
import AudioPlayer from "../../../specific/utils/AudioPlayer/AudioPlayer";
import { getAppTarget } from "../../../specific/utils/Navigation/Host";
import { getChapterAudioIdForActivityId, getDocumentUrlForActivityId } from "../../../specific/services/AppWrite/AppWriteDatabase";
import { analyticsEventActivityCompleted, analyticsEventActivityLaunch, propertiesEventAccess, propertiesEventCategory, propertiesEventContent, propertiesEventContentName, propertiesEventName, trackEvent } from "../../services/Tracking/Analytics";
import { IcKeyImages } from "../../utils/Icons";
import SVGIconButton from "../../designSystem/Buttons/SVGIconButton";
import Images from "../../../specific/utils/Images";
import ImagesSlideshow from "../../../specific/components/Images/ImagesSlideshow";

interface AudioChapterProps {
    adapted?: boolean,
    onSelectionDone: (data: any) => void,
    chapters: string[],
    chaptersImages?: { app: string, db: string }[][],
    urls: string[],
    localImage?: string,
    remoteUrl?: string,
    title: string,
    color: string,
    navColor?: string,
    activityId: string,
    onActivityCompleted: () => void,
    categoryAnalyticsKey: string, 
    isPremium: boolean, 
    activityAnalyticsKey: string
}

const AudioChapter = forwardRef((props: AudioChapterProps, ref) => {

    const [viewDidAppear, setViewDidAppear] = useState<boolean>(false);
    const [imageToDisplay, setImageToDisplay] = useState<{ app: string, db: string } | undefined>(undefined);
    const [showImageModal, setShowImageModal] = useState<boolean>(false);
    const appTheme: AppTheme = new AppTheme();
    const images: Images = new Images();

    const screenWidth: number = Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth();
    const { chapters, urls, onSelectionDone, localImage, remoteUrl, title, color, activityId, onActivityCompleted, adapted = false, chaptersImages, categoryAnalyticsKey, isPremium, activityAnalyticsKey } = props;
    const [selectedIndex, setSelectedIndex] = useState<number>(0);

    const audioPlayerRef = useRef(null);

    useImperativeHandle(ref, () => ({
        onCloseRequest: () => {
            if (audioPlayerRef.current !== null) {
                audioPlayerRef.current.onStopRequest();
            }
        }
    }));

    const onPress = (index: string) => {
        if (audioPlayerRef.current !== null) {
            audioPlayerRef.current.onStopRequest();
        }
        setSelectedIndex(parseInt(index));
    }

    const mobileMediaCell = ({ item, index }) => {
        const isFirst = parseInt(index) === 0;
        const isLast = parseInt(index) + 1 === urls.length;
        return <View style={{ alignItems: "center" }}>
            {mediaCell({ media: item, isFirst, isLast, index })}
        </View>
    }

    const onActivityCompletedForGamification = () => {
        onActivityCompleted();
    }

    const onPlayerEnd = (index: number) => {
        if (urls[index + 1] !== undefined) {
            setSelectedIndex(index + 1);
        } else {
            setSelectedIndex(-1);
        }
    }

    const getAudioPlayerView = (data: { url: string, index: number }) => {
        const { url, index } = data;
        return <View style={{ justifyContent: "center", alignItems: "center" }}>
            <AudioPlayer
                ref={audioPlayerRef}
                size={appTheme.pixelPerfect(48)}
                onActivityCompleted={onActivityCompletedForGamification}
                percentageForCompletion={70}
                onPlayerEnd={() => onPlayerEnd(index)}
                stopRequest={false}
                autoLaunch={true}
                color={props.color}
                urls={[url]}
                title={props.title !== undefined ? props.title : "Lili"}
                image={props.remoteUrl} />
        </View>
    }

    useEffect(() => {
        if (imageToDisplay !== undefined) {
            trackEvent(analyticsEventActivityLaunch, {
                [propertiesEventCategory]: categoryAnalyticsKey,
                [propertiesEventContent]: "key_images",
                [propertiesEventContentName]: imageToDisplay.app,
                [propertiesEventAccess]: isPremium === true ? "premium" : "free",
                [propertiesEventName]: adapted === true ? activityAnalyticsKey + "_ADAPTED" : activityAnalyticsKey
            });
            setShowImageModal(true);
        } else {
            setShowImageModal(false);
        }
    }, [imageToDisplay]);

    const showImage = (image: { app: string, db: string }) => {
        // On va déterminer si on montre l'image locale ou l'image présente sur le serveur
        setImageToDisplay(image);
    }

    const renderImageView = () => {
        if (showImageModal === false) {
            return <View />
        }
        return <ImagesSlideshow slideshowImages={[imageToDisplay]} color={color} closeTheGame={() => setImageToDisplay(undefined)} />
    }

    const mediaCell = (data: { media: any, isFirst: boolean, isLast: boolean, index: string }) => {
        const { media, isFirst, isLast, index } = data;

        const playButtonSize = appTheme.pixelPerfect(48);
        const playButtonMargin: number = appTheme.pixelPerfect(6);
        const nextButtonSize: number = playButtonSize - 2 * playButtonMargin;
        const bottomLine = isLast === true ? <View /> : <View style={{ position: "absolute", width: 1, height: playButtonSize, bottom: 0, left: playButtonSize / 2, backgroundColor: color }} />;
        const topLine = isFirst === true ? <View /> : <View style={{ position: "absolute", width: 1, height: playButtonSize, top: 0, left: playButtonSize / 2, backgroundColor: color }} />;
        const mediaCellWidth = Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth();

        let playerButton = <TouchableOpacity onPress={() => onPress(index)} style={{ marginStart: playButtonMargin, borderWidth: 1, borderColor: color, width: nextButtonSize, height: nextButtonSize, borderRadius: nextButtonSize / 2, justifyContent: "center", alignItems: "center", backgroundColor: appTheme.white, marginEnd: appTheme.pixelPerfect(15) }}>
            <SVGView size={appTheme.pixelPerfect(14)} Component={IcPlay} color={color} />
        </TouchableOpacity>;
        let titleSize: number = appTheme.pixelPerfectForFont(9);
        let titleColor: string = color;
        if (selectedIndex === parseInt(index)) {
            titleSize = appTheme.pixelPerfectForFont(10);
            titleColor = color;
            playerButton = <View style={{ marginEnd: appTheme.pixelPerfect(15) }}>
                {getAudioPlayerView({ url: media.url, index: parseInt(index) })}
            </View>
        }
        let bodyView = <View />;
        if (media.chapterBody.length > 0) {
            bodyView = <Text style={{ width: "100%", textAlign: "left", color: appTheme.darkBlue, opacity: 1, fontFamily: appTheme.primarySemiBoldFont, fontSize: titleSize }}>{media.chapterBody}</Text>
        }

        let imageIconView: React.JSX.Element = <View />;
        if (media.chapterImages.length > 0) {
            let imagesView: React.JSX.Element[] = [];
            for (const aChapterImageIndex in media.chapterImages) {
                if (Object.prototype.hasOwnProperty.call(media.chapterImages, aChapterImageIndex)) {
                    const aChapterImage = media.chapterImages[aChapterImageIndex];
                    if (imagesView.length > 0) {
                        imagesView.push(<View style={{ width: appTheme.pixelPerfect(4) }} />);
                    }
                    imagesView.push(<SVGIconButton onPress={() => showImage(aChapterImage)} shadow={false} Icon={IcKeyImages} color={appTheme.white} style={{ backgroundColor: color }} />);
                }
            }
            imageIconView = <View style={{ flexDirection: "row" }}>
                {imagesView}
            </View>
        }
        const marginBottom: number = isLast === true ? appTheme.pixelPerfect(40) + appTheme.bottomInsets : 0;
        return <View style={{ marginBottom, flexDirection: "row", height: appTheme.pixelPerfect(70), justifyContent: "flex-start", alignItems: "center", width: mediaCellWidth * 0.9 }}>
            {topLine}
            {bottomLine}
            {playerButton}
            <View style={{ flex: 1, paddingRight: appTheme.pixelPerfect(10), alignItems: "center", justifyContent: "center" }}>
                <Text numberOfLines={3} style={{ width: "100%", textAlign: "left", color: titleColor, opacity: 1, fontFamily: appTheme.primaryBoldFont, fontSize: titleSize }}>{media.chapterTitle}</Text>
                {bodyView}
            </View>
            {imageIconView}
        </View>
    }

    const getChapterList = () => {
        const chaptersData = [];
        for (const aUrlIndex in urls) {
            if (Object.prototype.hasOwnProperty.call(urls, aUrlIndex)) {
                let anAudioUrl: string = urls[aUrlIndex];
                const appTarget = getAppTarget();
                if (appTarget !== "public") {
                    anAudioUrl = getChapterAudioIdForActivityId({ activity_id: activityId, index: aUrlIndex, isAdapted: adapted });
                }
                const chapterNumber: string = (parseInt(aUrlIndex) + 1).toString();
                let chapterTitle: string = "Chapitre " + chapterNumber;
                if ((chapters[aUrlIndex] !== undefined) && (chapters[aUrlIndex].title !== undefined) && (chapters[aUrlIndex].title[getLanguage()] !== undefined)) {
                    chapterTitle = chapters[aUrlIndex].title[getLanguage()];
                }
                let chapterBody: string = ((chapters[aUrlIndex] !== undefined) && (chapters[aUrlIndex].body !== undefined) && (chapters[aUrlIndex].body[getLanguage()] !== undefined)) ? chapters[aUrlIndex].body[getLanguage()] : "";
                let chapterImages: { app: string, db: string }[] = [];
                if ((chaptersImages !== undefined) && (chaptersImages[aUrlIndex] !== undefined) && (chaptersImages[aUrlIndex].length > 0)) {
                    // s'il n'y a aucune image, on aura un seul objet avec une chaine de caractère vide pour app et db
                    if ((chaptersImages[aUrlIndex][0].app !== "") || (chaptersImages[aUrlIndex][0].db !== "")) {
                        // On a au moins une image
                        chapterImages = chaptersImages[aUrlIndex];
                    }
                }

                chaptersData.push({
                    url: anAudioUrl,
                    chapterTitle,
                    chapterBody,
                    chapterImages
                })
            }
        }

        if (Platform.OS === "web") {
            let mediaListView = [];
            for (const aMediaIndex in chaptersData) {
                if (Object.prototype.hasOwnProperty.call(chaptersData, aMediaIndex)) {
                    const aMedia = chaptersData[aMediaIndex];
                    const isFirst = parseInt(aMediaIndex) === 0;
                    const isLast = parseInt(aMediaIndex) + 1 === chaptersData.length;
                    mediaListView.push(mediaCell({ media: aMedia, isFirst, isLast, index: aMediaIndex }));
                }
            }
            return <View style={{ width: appTheme.getBlockWidth(), alignItems: "center" }}>
                <View style={{ marginTop: appTheme.pixelPerfect(20), width: appTheme.getBlockWidth(), alignItems: "center" }}>
                    {mediaListView}
                </View>
            </View>
        }
        return <FlatList
            data={chaptersData}
            style={{ width: appTheme.getFullAppWidth() }}
            renderItem={mobileMediaCell}
            keyExtractor={(item, index) => item.chapterTitle} />
    }

    let imageWidth: number = screenWidth;
    let imageHeight: number = 270 * imageWidth / 360;
    if (imageHeight > appTheme.pixelPerfect(200)) {
        imageHeight = appTheme.pixelPerfect(200);
        imageWidth = 360 * imageHeight / 270;
    }
    return <View style={[styles.container, { width: screenWidth }]}>
        <View style={{ marginBottom: appTheme.pixelPerfect(10), width: screenWidth, height: imageHeight, backgroundColor: "#FFFFFF", alignItems: "center", paddingVertical: appTheme.pixelPerfect(10), borderRadius: appTheme.pixelPerfect(10) }}>
            <Image source={{ uri: remoteUrl }} style={{ width: imageWidth, height: imageHeight, borderRadius: appTheme.pixelPerfect(10) }} resizeMode="cover" />
        </View>
        {getChapterList()}
        <Modal animationType="slide" visible={showImageModal} transparent={false} style={{ flex: 1 }}>
            <View style={{ backgroundColor: color + "80", width: appTheme.getFullScreenWidth(), height: appTheme.getFullScreenHeight() }}>
                {renderImageView()}
            </View>
        </Modal>
    </View>

});

export default AudioChapter;

const styles = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center"
    }
})